import React from "react";
import axios from "axios"; // Import axios for fetching
import BackButton from "../common/BackButton"; // Assuming BackButton is in common
import { Button } from "@jammable/ui-core";
import { BsCheckCircleFill } from "react-icons/bs";
import Tabs from "../Tabs"; // Import the Tabs component
import { useQuery } from "react-query";
import { Spinner } from "@jammable/ui-components/Loader"; // Import Spinner

// --- Exportable Presets ---
// export const effectPresets = [
// ... (This section will be removed, including all hardcoded presets)
// ];

// --- Component Definition ---
const AudioEffectsSelector = ({ onBack, selectedEffectId, onSelectEffect }) => {
  const [activeTabId, setActiveTabId] = React.useState("full_song");

  const {
    data: effectPresets,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["effectPresets"],
    queryFn: async () => {
      const { data } = await axios.get("/api/effects/getEffects");
      if (!data.success) {
        throw new Error(data.message || "Failed to fetch effects");
      }
      return data.data;
    },
  });

  const displayCategories = [
    { id: "full_song", name: "Full Song Effects" },
    { id: "vocals_only", name: "Vocal Only Effects" },
  ];

  const renderPresetCard = (preset, isSelectedFlag) => (
    <div
      key={preset._id}
      onClick={() => {
        if (selectedEffectId === preset._id) {
          onSelectEffect(null);
        } else {
          onSelectEffect(preset._id);
        }
      }}
      className={`tw-relative tw-text-wrap tw-w-full tw-max-w-[200px] tw-rounded-2xl tw-p-3 tw-cursor-pointer tw-transition-all tw-duration-100 
        ${
          isSelectedFlag
            ? `${preset.theme?.backgroundColor || "tw-bg-gradient-to-br tw-bg-purple-100 from-purple-50 to-indigo-50"}  tw-scale-[1.03]`
            : preset.theme?.backgroundColor || "tw-bg-gray-50"
        }`}
    >
      {isSelectedFlag && (
        <BsCheckCircleFill
          className={`tw-absolute tw-top-2 tw-right-2 tw-text-xl ${preset.theme?.textColor || "tw-text-purple-700"}`}
        />
      )}
      {/* <div className="tw-text-xs tw-bg-black/5 tw-font-bold tw-mb-2 tw-w-max tw-px-2 tw-py-0.5 tw-rounded-md tw-flex tw-items-center tw-gap-0.5">
        By Jammable
        <MdVerified className="tw-text-green-500" />
      </div> */}

      <div className=" tw-mt-1">
        <div className="tw-text-xs tw-font-black tw-tracking-widest tw-text-black/40 tw-text-wrap tw-mb-1.5">
          {preset.uses} USES
        </div>
        <div
          className={`tw-font-black tw-tracking-wider tw-text-xl tw-uppercase ${isSelectedFlag ? preset.theme?.textColor || "tw-text-purple-700" : preset.theme?.textColor || "tw-text-black"}`}
        >
          <span style={{ fontSize: 32 }}>{preset.emoji} </span>

          {preset.name}
        </div>
        {preset.description && (
          <div className="tw-text-xs tw-font-semibold tw-text-black/40 tw-text-wrap tw-mt-0.5">
            {preset.description}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="tw-bg-white tw-p-5 tw-rounded-3xl tw-shadow-lg tw-border tw-border-gray-200 tw-border-solid">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <BackButton
            onClick={onBack}
            aria-label="Back to main creation step"
          />
          <h2 className="tw-text-lg tw-my-0 tw-tracking-widest tw-font-black tw-text-black/40 tw-uppercase">
            Select Effects
          </h2>
        </div>
      </div>

      {/* Content Area: Loader, Error, Empty, or Effects List */}
      <div className="tw-min-h-[200px]">
        {error && (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-full tw-text-red-500">
            <p>Error loading effects: {error.message}</p>
          </div>
        )}

        <>
          <div className=" tw-w-full tw-flex tw-justify-center">
            <Tabs
              tabs={displayCategories.map((category) => ({
                id: category.id,
                label: category.name,
              }))}
              selectedTab={activeTabId}
              onTabChange={setActiveTabId}
              className="tw-mb-4"
            />
          </div>

          {isLoading && (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-full tw-my-4">
              <Spinner size={4} className="tw-border-purple-700 tw-border-3" />
            </div>
          )}

          {displayCategories
            .filter((category) => category.id === activeTabId)
            .map((category) => (
              <div
                key={category.id}
                className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2 tw-overflow-y-auto tw-max-h-[300px] md:tw-max-h-[400px]"
              >
                {effectPresets
                  ?.filter(
                    (preset) =>
                      preset.displaySections &&
                      preset.displaySections.length > 0 && // Ensure displaySections is not empty
                      preset.displaySections.includes(category.id), // Keep user's preferred filter logic
                  )
                  ?.map((preset) =>
                    renderPresetCard(preset, selectedEffectId === preset._id),
                  )}
              </div>
            ))}
        </>
      </div>

      {/* Done Button */}
      <Button
        onClick={onBack}
        className="tw-mt-4 tw-w-full tw-text-lg tw-uppercase tw-rounded-2xl tw-font-black tw-h-12 tw-border tw-border-solid tw-border-purple-700 tw-border-b-4 tw-border-b-purple-900 active:tw-translate-y-0.5 active:tw-border-b-2 active:tw-scale-[0.98] tw-transition tw-duration-100 tw-ease-in-out disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
      >
        Done
      </Button>
    </div>
  );
};

export default AudioEffectsSelector;
